import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Database, Layers, FileText, ArrowRight } from 'lucide-react';
import { ragService } from '../services/api';
import { getUser, isAuthenticated } from '../services/auth';

const RAG_ORGS = [
  {
    name: 'Selegic',
    description: 'Primary corporate workspace containing core guidelines, articles, policies, and main lexicon databases.',
    badge: 'Product Consultancy',
    iconName: 'database',
    category: 'Corporate Info',
  },
  {
    name: 'SelegicCPQ',
    description: 'Configure, Price, Quote workspace dedicated to sales catalogs, price sheets, and service contracts.',
    badge: 'CPQ Domain',
    iconName: 'layers',
    category: 'Sales Enablement',
  }
];

function RagLauncher() {
  const user = getUser();
  const [loadingOrg, setLoadingOrg] = useState('');
  const [error, setError] = useState('');

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  const launch = async (ragOrgName) => {
    setError('');
    setLoadingOrg(ragOrgName);
    const tab = window.open('', '_blank');
    try {
      const response = await ragService.createSession(ragOrgName);
      if (tab) {
        tab.location = response.data.launchUrl;
      } else {
        window.location.href = response.data.launchUrl;
      }
    } catch (err) {
      if (tab) {
        tab.close();
      }
      setError(err.response?.data?.error || 'Could not open RAG');
    } finally {
      setLoadingOrg('');
    }
  };

  const getIcon = (name) => {
    switch (name) {
      case 'database':
        return <Database size={20} strokeWidth={2} />;
      case 'layers':
        return <Layers size={20} strokeWidth={2} />;
      default:
        return <FileText size={20} strokeWidth={2} />;
    }
  };

  return (
    <div className="rag-entry">
      <div className="rag-entry-header">
        <div className="rag-header-left">
          <div className="rag-entry-icon">
            <Database size={24} strokeWidth={1.5} />
          </div>
          <div className="rag-header-title-block">
            <h2>RAG Knowledge Workspaces</h2>
            <p>Select a workspace broker to access your semantic indices and documents.</p>
          </div>
        </div>

        <div className="rag-header-user-badge">
          <span className="rag-header-status-dot"></span>
          <span className="rag-header-user-text">
            Connected: <strong>{[user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email}</strong>
          </span>
        </div>
      </div>

      <div className="rag-entry-grid">
        {RAG_ORGS.map((org) => {
          const isOpening = loadingOrg === org.name;
          return (
            <button
              type="button"
              key={org.name}
              className={`rag-entry-card ${isOpening ? 'is-launching' : ''}`}
              onClick={() => launch(org.name)}
              disabled={Boolean(loadingOrg)}
            >
              <div className="rag-card-top">
                <div className="rag-card-icon-wrapper">
                  {getIcon(org.iconName)}
                </div>
                <div className="rag-card-meta">
                  <span className="rag-card-category">{org.category}</span>
                  <span className="rag-card-badge">{org.badge}</span>
                </div>
              </div>

              <div className="rag-card-content">
                <h3>{org.name}</h3>
                <p>{org.description}</p>
              </div>

              <div className="rag-card-footer">
                <span className="rag-card-action-text">
                  {isOpening ? 'Connecting to Organization...' : 'Launch Organization'}
                </span>
                <ArrowRight size={16} className="rag-card-arrow" />
              </div>
            </button>
          );
        })}
      </div>

      {error && <div className="rag-entry-error">{error}</div>}
    </div>
  );
}

export default RagLauncher;
